import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { UserGroupIcon, TrophyIcon, UserIcon, AcademicCapIcon } from '@heroicons/react/24/outline';
import { getApiUrl } from '../../utils/api';
import ImageModal from '../../components/ImageModal';

import { getImageUrlWithEnv } from '../../utils/imageUrl';
const FirstTeam = () => {
  const [players, setPlayers] = useState([]);
  const [coachingStaff, setCoachingStaff] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedImage, setSelectedImage] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        
        // Fetch players and coaching staff in parallel
        const [playersResponse, coachingStaffResponse] = await Promise.all([
          fetch(getApiUrl('/api/players/?team_type=first_team')),
          fetch(getApiUrl('/api/coaching-staff/first-team'))
        ]);

        if (!playersResponse.ok) {
          throw new Error('Failed to fetch players');
        }
        if (!coachingStaffResponse.ok) {
          throw new Error('Failed to fetch coaching staff');
        }

        const playersData = await playersResponse.json();
        const coachingStaffData = await coachingStaffResponse.json();

        console.log('Players data:', playersData);
        console.log('Coaching staff data:', coachingStaffData);

        setPlayers(playersData);
        setCoachingStaff(coachingStaffData);
      } catch (err) {
        setError(err.message);
        console.error('Error fetching data:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const positions = {
    "მეკარე": players.filter(p => p.position === "მეკარე"),
    "მცველი": players.filter(p => p.position === "მცველი"),
    "ნახევარმცველი": players.filter(p => p.position === "ნახევარმცველი"),
    "თავდამსხმელი": players.filter(p => p.position === "თავდამსხმელი")
  };

  const getPositionPlural = (position) => {
    switch (position) {
      case "მეკარე": return "მეკარეები";
      case "მცველი": return "მცველები";
      case "ნახევარმცველი": return "ნახევარმცველები";
      case "თავდამსხმელი": return "თავდამსხმელები";
      default: return position + "ები";
    }
  };

  // Filter out coaches assigned to women's team
  const filteredCoachingStaff = coachingStaff.filter(coach => {
    // Check if coach has any team assignments with team_type "womens_team"
    const hasWomensTeamAssignment = coach.team_assignments && 
      coach.team_assignments.some(assignment => assignment.team_type === "womens_team");
    
    // Exclude coaches with women's team assignments
    return !hasWomensTeamAssignment;
  });

  // Custom sorting to ensure specific coaches appear first
  const sortedCoachingStaff = filteredCoachingStaff.sort((a, b) => {
    const priorityOrder = ['ზურაბ ლომიძე', 'ზაზა ლაცაბიძე'];
    
    const aIndex = priorityOrder.indexOf(a.name);
    const bIndex = priorityOrder.indexOf(b.name);
    
    // If both are in priority list, sort by priority order
    if (aIndex !== -1 && bIndex !== -1) {
      return aIndex - bIndex;
    }
    
    // If only 'a' is in priority list, 'a' comes first
    if (aIndex !== -1) {
      return -1;
    }
    
    // If only 'b' is in priority list, 'b' comes first
    if (bIndex !== -1) {
      return 1;
    }
    
    // If neither is in priority list, maintain original order
    return 0;
  });

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto mb-4"></div>
          <p className="text-gray-600 font-georgian">მონაცემების ჩატვირთვა...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <p className="text-red-600 font-georgian mb-4">შეცდომა: {error}</p>
          <button 
            onClick={() => window.location.reload()} 
            className="bg-primary-600 hover:bg-primary-700 text-white font-bold py-2 px-4 rounded font-georgian"
          >
            ხელახლა ცდა
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="text-4xl font-bold text-gray-900 font-georgian mb-4">
            პირველი გუნდი
          </h1>
          <p className="text-xl text-gray-600 font-georgian max-w-3xl mx-auto">
            ჩვენი მთავარი გუნდის ფეხბურთელები და მათი სტატისტიკა
          </p>
        </div>

        {/* Stats Overview */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-12">
          <div className="bg-white rounded-lg shadow-lg p-6 text-center">
            <UserGroupIcon className="h-8 w-8 text-primary-600 mx-auto mb-2" />
            <h3 className="text-lg font-semibold text-gray-900 font-georgian">ფეხბურთელები</h3>
            <p className="text-2xl font-bold text-primary-600">{players.length}</p>
          </div>
          <div className="bg-white rounded-lg shadow-lg p-6 text-center">
            <TrophyIcon className="h-8 w-8 text-yellow-600 mx-auto mb-2" />
            <h3 className="text-lg font-semibold text-gray-900 font-georgian">საშუალო ასაკი</h3>
            <p className="text-2xl font-bold text-yellow-600">
              {players.filter(p => p.age !== null).length > 0 
                ? Math.round(players.filter(p => p.age !== null).reduce((sum, p) => sum + p.age, 0) / players.filter(p => p.age !== null).length)
                : 'N/A'
              }
            </p>
          </div>
          <div className="bg-white rounded-lg shadow-lg p-6 text-center">
            <div className="h-8 w-8 bg-green-600 rounded-full mx-auto mb-2 flex items-center justify-center">
              <span className="text-white font-bold text-sm">🇬🇪</span>
            </div>
            <h3 className="text-lg font-semibold text-gray-900 font-georgian">ქართველები</h3>
            <p className="text-2xl font-bold text-green-600">{players.filter(p => p.nationality === "საქართველო").length}</p>
          </div>
          <div className="bg-white rounded-lg shadow-lg p-6 text-center">
            <div className="h-8 w-8 bg-blue-600 rounded-full mx-auto mb-2 flex items-center justify-center">
              <span className="text-white font-bold text-sm">⚽</span>
            </div>
            <h3 className="text-lg font-semibold text-gray-900 font-georgian">პოზიციები</h3>
            <p className="text-2xl font-bold text-blue-600">{Object.keys(positions).length}</p>
          </div>
        </div>

        {/* Players by Position */}
        {Object.entries(positions).map(([position, positionPlayers]) => (
          <div key={position} className="bg-white rounded-lg shadow-lg p-8 mb-8">
            <h2 className="text-2xl font-bold text-gray-900 font-georgian mb-6">
              {getPositionPlural(position)} ({positionPlayers.length})
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
              {positionPlayers.map((player, index) => (
                <div key={index} className="bg-gray-50 rounded-lg overflow-hidden hover:shadow-lg transition-all duration-300 hover:scale-105">
                  {/* Player Photo */}
                  <div className="relative h-48 bg-gray-200 cursor-pointer" onClick={() => player.photo_url && setSelectedImage({
                    imageUrl: getImageUrlWithEnv(player.photo_url),
                    alt: player.name,
                    title: player.name,
                    subtitle: `${player.position} • ${player.nationality}`
                  })}>
                    {player.photo_url ? (
                      <img
                        src={getImageUrlWithEnv(player.photo_url)}
                        alt={player.name}
                        className="w-full h-full object-cover hover:scale-105 transition-transform duration-300"
                        onError={(e) => {
                          console.log('Player image error:', player.photo_url, '->', getImageUrlWithEnv(player.photo_url));
                          e.target.style.display = 'none';
                          e.target.nextSibling.style.display = 'flex';
                        }}
                        onLoad={() => {
                          console.log('Player image loaded:', player.photo_url, '->', getImageUrlWithEnv(player.photo_url));
                        }}
                      />
                    ) : null}
                    <div 
                      className={`w-full h-full ${player.photo_url ? 'hidden' : 'flex'} items-center justify-center bg-gradient-to-br from-primary-500 to-primary-700`}
                    >
                      <UserIcon className="h-16 w-16 text-white" />
                    </div>
                    
                    {/* Player Number Badge */}
                    {player.number && (
                      <div className="absolute top-4 right-4 bg-white bg-opacity-90 rounded-full w-10 h-10 flex items-center justify-center">
                        <span className="text-lg font-bold text-gray-900">{player.number}</span>
                      </div>
                    )}
                  </div>
                  
                  {/* Player Info */}
                  <div className="p-4">
                    <h3 className="font-bold text-gray-900 font-georgian text-lg mb-1">{player.name}</h3>
                    <p className="text-sm text-primary-600 font-georgian mb-2">{player.position}</p>
                    
                    <div className="space-y-1 text-sm text-gray-600 font-georgian">
                      {player.age && (
                        <p>ასაკი: {player.age} წელი</p>
                      )}
                      <p>{player.nationality}</p>
                      {player.height && (
                        <p>სიმაღლე: {player.height} სმ</p>
                      )}
                      {player.weight && (
                        <p>წონა: {player.weight} კგ</p>
                      )}
                    </div>
                    
                    {/* Bio Preview */}
                    {player.bio && (
                      <div className="mt-3 pt-3 border-t border-gray-200">
                        <p className="text-xs text-gray-500 font-georgian line-clamp-2">
                          {player.bio.length > 100 ? `${player.bio.substring(0, 100)}...` : player.bio}
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        ))}

        {/* Coaching Staff */}
        <div className="bg-white rounded-lg shadow-lg p-8 mb-8">
          <h2 className="text-2xl font-bold text-gray-900 font-georgian mb-6">
            პირველი გუნდის სამწვრთნელო შტაბი
          </h2>
          {coachingStaff.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
              {sortedCoachingStaff.map((coach, index) => (
                <div key={index} className="bg-gray-50 rounded-lg overflow-hidden hover:shadow-lg transition-all duration-300 hover:scale-105">
                  {/* Coach Photo */}
                  <div className="relative h-48 bg-gray-200 cursor-pointer" onClick={() => coach.photo_url && setSelectedImage({
                    imageUrl: getImageUrlWithEnv(coach.photo_url),
                    alt: coach.name,
                    title: coach.name,
                    subtitle: `${coach.position} • ${coach.nationality}`
                  })}>
                    {coach.photo_url ? (
                      <img
                        src={getImageUrlWithEnv(coach.photo_url)}
                        alt={coach.name}
                        className="w-full h-full object-cover hover:scale-105 transition-transform duration-300"
                        onError={(e) => {
                          console.log('Coach image error:', coach.photo_url, '->', getImageUrlWithEnv(coach.photo_url));
                          e.target.style.display = 'none';
                          e.target.nextSibling.style.display = 'flex';
                        }}
                        onLoad={() => {
                          console.log('Coach image loaded:', coach.photo_url, '->', getImageUrlWithEnv(coach.photo_url));
                        }}
                      />
                    ) : null}
                    <div 
                      className={`w-full h-full ${coach.photo_url ? 'hidden' : 'flex'} items-center justify-center bg-gradient-to-br from-primary-500 to-primary-700`}
                    >
                      <UserIcon className="h-16 w-16 text-white" />
                    </div>
                  </div>
                  
                  {/* Coach Info */}
                  <div className="p-4">
                    <h3 className="font-bold text-gray-900 font-georgian text-lg mb-1">{coach.name}</h3>
                    <p className="text-sm text-primary-600 font-georgian mb-2">{coach.position}</p>
                    
                    <div className="space-y-1 text-sm text-gray-600 font-georgian">
                      {coach.age && (
                        <p>ასაკი: {coach.age} წელი</p>
                      )}
                      <p>{coach.nationality}</p>
                      {coach.experience && (
                        <p>გამოცდილება: {coach.experience}</p>
                      )}
                      {coach.license && (
                        <p>ლიცენზია: {coach.license}</p>
                      )}
                    </div>
                    
                    {coach.description && (
                      <p className="mt-3 text-xs text-gray-500 font-georgian line-clamp-2">{coach.description}</p>
                    )}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8">
              <AcademicCapIcon className="h-16 w-16 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-500 font-georgian">სამწვრთნელო შტაბის ინფორმაცია მალე დაემატება</p>
            </div>
          )}
        </div>

        {/* Navigation */}
        <div className="text-center">
          <Link
            to="/team"
            className="inline-flex items-center bg-primary-600 hover:bg-primary-700 text-white font-bold py-3 px-6 rounded-lg transition-colors duration-200 font-georgian"
          >
            ← უკან გუნდის გვერდზე
          </Link>
        </div>
      </div>
      
      {/* Image Modal */}
      <ImageModal
        isOpen={selectedImage !== null}
        onClose={() => setSelectedImage(null)}
        imageUrl={selectedImage?.imageUrl}
        alt={selectedImage?.alt}
        title={selectedImage?.title}
        subtitle={selectedImage?.subtitle}
      />
    </div>
  );
};

export default FirstTeam;